import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';

/**
 * Admin status page (docs/ops/STATUS-PAGE.md).
 *
 * One row per watched thing - our own apps, every third-party provider,
 * and the infrastructure underneath - with its current state, when it
 * last changed, and the incident log that says how long it was down and
 * what the probe saw. Probes run from the API (`status.probe-*` crons)
 * and, for the API itself, from an AWS Lambda outside the box that
 * reports in through `POST /status/external`.
 */

export const SERVICE_HEALTH_STATES = ['UP', 'DEGRADED', 'DOWN', 'UNKNOWN'] as const;
export const ServiceHealthStateSchema = z.enum(SERVICE_HEALTH_STATES);
export type ServiceHealthState = z.infer<typeof ServiceHealthStateSchema>;

export const SERVICE_GROUPS = ['APP', 'PROVIDER', 'INFRA', 'EXTERNAL'] as const;
export const ServiceGroupSchema = z.enum(SERVICE_GROUPS);
export type ServiceGroup = z.infer<typeof ServiceGroupSchema>;

export const SERVICE_INCIDENT_SOURCES = ['PROBE', 'EXTERNAL', 'MANUAL'] as const;
export const ServiceIncidentSourceSchema = z.enum(SERVICE_INCIDENT_SOURCES);
export type ServiceIncidentSource = z.infer<typeof ServiceIncidentSourceSchema>;

export const ServiceIncidentSchema = z.object({
  id: UuidSchema,
  serviceKey: z.string(),
  serviceName: z.string(),
  /** DOWN or DEGRADED - the worst state seen during the incident. */
  severity: ServiceHealthStateSchema,
  startedAt: IsoDateTimeSchema,
  /** Null while still open. */
  endedAt: IsoDateTimeSchema.nullable(),
  /** Up to now for an open incident. */
  durationSeconds: z.number().int().nonnegative(),
  /** What the probe saw: HTTP code, timeout, auth rejected, "no webhook for 45 min"... */
  cause: z.string(),
  /** Admin's own root-cause / follow-up note. */
  note: z.string().nullable(),
  source: ServiceIncidentSourceSchema,
});
export type ServiceIncident = z.infer<typeof ServiceIncidentSchema>;

export const ServiceStatusItemSchema = z.object({
  key: z.string(),
  group: ServiceGroupSchema,
  name: z.string(),
  description: z.string(),
  status: ServiceHealthStateSchema,
  /** When the current state began. */
  since: IsoDateTimeSchema.nullable(),
  lastCheckedAt: IsoDateTimeSchema.nullable(),
  lastLatencyMs: z.number().int().nullable(),
  /** Last failure text, kept while the service is failing. */
  lastError: z.string().nullable(),
  /** Extra read-out from the last probe (balance, queue depth, ...). */
  detail: z.string().nullable(),
  intervalSeconds: z.number().int().positive(),
  /** Share of the last 24 h / 30 d not inside a DOWN or DEGRADED incident, 0-100. */
  uptime24h: z.number().nullable(),
  uptime30d: z.number().nullable(),
  openIncident: ServiceIncidentSchema.nullable(),
});
export type ServiceStatusItem = z.infer<typeof ServiceStatusItemSchema>;

export const StatusOverviewSchema = z.object({
  overall: ServiceHealthStateSchema,
  generatedAt: IsoDateTimeSchema,
  services: z.array(ServiceStatusItemSchema),
});
export type StatusOverview = z.infer<typeof StatusOverviewSchema>;

export const ServiceDayBucketSchema = z.object({
  /** YYYY-MM-DD in Asia/Kolkata. */
  date: z.string(),
  /** Worst state that day, UNKNOWN when nothing was recorded. */
  worst: ServiceHealthStateSchema,
  /** Seconds spent in DOWN / DEGRADED incidents that day. */
  downSeconds: z.number().int().nonnegative(),
  incidents: z.number().int().nonnegative(),
});

export type ServiceDayBucket = z.infer<typeof ServiceDayBucketSchema>;

export const ServiceHistorySchema = z.object({
  key: z.string(),
  name: z.string(),
  days: z.array(ServiceDayBucketSchema),
  /** Raw probe samples of the last 24 h, oldest first. */
  samples: z.array(
    z.object({
      at: IsoDateTimeSchema,
      status: ServiceHealthStateSchema,
      latencyMs: z.number().int().nullable(),
      error: z.string().nullable(),
    }),
  ),
  incidents: z.array(ServiceIncidentSchema),
});
export type ServiceHistory = z.infer<typeof ServiceHistorySchema>;

export const StatusIncidentsQuerySchema = z.object({
  serviceKey: z.string().optional(),
  openOnly: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type StatusIncidentsQuery = z.infer<typeof StatusIncidentsQuerySchema>;

export const StatusIncidentsResponseSchema = z.object({
  items: z.array(ServiceIncidentSchema),
});
export type StatusIncidentsResponse = z.infer<typeof StatusIncidentsResponseSchema>;

export const StatusIncidentNoteSchema = z.object({
  note: z.string().max(2000),
});
export type StatusIncidentNote = z.infer<typeof StatusIncidentNoteSchema>;

export const STATUS_CHECK_GROUPS = ['fast', 'slow', 'all'] as const;
export const StatusCheckNowSchema = z.object({
  group: z.enum(STATUS_CHECK_GROUPS).default('all'),
});
export type StatusCheckNow = z.infer<typeof StatusCheckNowSchema>;

export const StatusCheckNowResponseSchema = z.object({
  checked: z.number().int().nonnegative(),
  opened: z.array(z.string()),
  resolved: z.array(z.string()),
});
export type StatusCheckNowResponse = z.infer<typeof StatusCheckNowResponseSchema>;

/**
 * What the outside watcher (infra/aws/lambda/status-watcher) posts every
 * minute: the view of our public URLs from outside AWS, plus any outage
 * it saw while the API itself was unreachable and could not be told.
 */
export const StatusExternalReportSchema = z.object({
  results: z
    .array(
      z.object({
        key: z.string().min(1).max(64),
        name: z.string().min(1).max(120),
        status: ServiceHealthStateSchema,
        latencyMs: z.number().int().nullable().optional(),
        error: z.string().max(500).nullable().optional(),
        checkedAt: IsoDateTimeSchema,
      }),
    )
    .max(50),
  outages: z
    .array(
      z.object({
        key: z.string().min(1).max(64),
        name: z.string().min(1).max(120),
        startedAt: IsoDateTimeSchema,
        endedAt: IsoDateTimeSchema.nullable(),
        cause: z.string().max(500),
      }),
    )
    .max(50)
    .default([]),
});
export type StatusExternalReport = z.infer<typeof StatusExternalReportSchema>;
